import * as React from 'react';
import { AppsSection } from '../apps-section';
import { BusinessHeader } from '../apps-section/apps-section-headers/business.apps-section-header';
import { RegularHeader } from '../apps-section/apps-section-headers/regular.apps-section-header';
import { CompleteSetupHeader } from '../apps-section/apps-section-headers/complete-setup.apps-section-header';
import { withTranslation } from '../../component-decorators/with-translation';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import {
  fetchBundleApps,
  fetchSiteData,
  getPermittedActions,
  newDeleteApp,
  setEmbeddedScriptAppActive,
  fetchInstalledApps,
  getAllEmbeddedScriptAppsStatus,
  fetchAppIdsByTag,
} from '../../api';
import webBiLogger from '@wix/web-bi-logger';
import {
  manageAppsAddApp,
  appMarketOpenManageApps,
} from '@wix/bi-logger-market/v2';
import type { IModalProps } from '../modals/modals';
import { ModalNames, ModalsNew } from '../modals/modals';
import { EmptyState } from '../empty-state';
import s from './manage-installed-apps-page.scss';
import { ChevronRight } from '@wix/wix-ui-icons-common';
import {
  Page,
  Loader,
  Button,
  Box,
  Heading,
  TextButton,
  Divider,
  WixDesignSystemProvider,
} from '@wix/design-system';
import { RecommendedApps } from '../empty-state/recommended-apps';
import { withExperiments } from '../../component-decorators/with-experiments';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { PermissionsCtx } from '../permissions-context/permissions-ctx';
import { MarketingIntegrationsToast } from '../modals/marketing-integrations-toast/marketing-integrations-toast';
import {
  appHasEditor,
  isBusinessApp,
  isRegularApp,
  getBuilderComps,
  isNewVersionAvailable,
  enableDeleteMLApp,
  isEmbeddedScriptApp,
} from '../../utils/appUtils';
import {
  newEnrichBundleApps,
  getDeepLink,
  getCompleteSetupApps,
} from '../../utils/utils';
import type { IAppInstallParams } from '@wix/installation-manager-client-sdk';
import type { BundleWebSolution } from '@wix/ambassador-marketplace/types';
import type { ManagedApp } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import {
  AppGroup,
  ComponentType,
} from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import { ModuleRegistry } from 'react-module-container';

export enum Origin {
  BIZMGR = 'bizmgr',
  EDITOR = 'editor',
  DAYFUL = 'dayful',
}

export enum embeddedScriptAppStatus {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  MISSING = 'missing',
  NO_STATUS = 'noStatus',
}

export enum EditorType {
  EDITOR = 'EDITOR',
  EDITORX = 'EDITORX',
  ADI = 'ADI',
  STUDIO = 'STUDIO',
}

export interface IEmbeddedScriptAppStatus {
  status: embeddedScriptAppStatus;
  parameters: object;
}

export interface IManageInstalledAppsPageProps
  extends Partial<WithTranslationProps>,
    Partial<InjectedExperimentsProps> {
  metasiteId: string;
  baseUrl: string;
  origin: Origin;
  editorUrl: string;
  manageAppsStaticsUrl: string;
  locale: string;
  isEditorless?: boolean;
  onLoaded?(): void;
  mainButtonAction(
    app: BundleWebSolution | ManagedApp,
    bundleApps: BundleWebSolution[],
    onInstallationSuccessCb?: IAppInstallParams['onInstallationSuccess'],
  ): Promise<{} | void>;

  goToHomePage?(): void;

  addAppAgain?(appDefID: string, appVersion: string): void;

  getAppInstance?(appDefID: string): string;

  gotoWebSolutionPage?(
    slug: string,
    referrer: string,
    impressionid?: string,
    referralSectionName?: string,
  ): void;

  editorType: string;

  showUserActionNotification?(INotificationsOptions);

  referralInfo?: string;

  unInstallApp?(appDefinitionId: string): Promise<any>;

  openApp?(app: BundleWebSolution | ManagedApp): Promise<{} | void>;

  openDeleteModalAppInEditor?(appId: string): void;

  deepLinkData?: { appId: string; modal: string };

  navigateToMarketingIntegration?: any;

  hideBusinessSolutions?: boolean;

  geo?: string;

  siteIsBusinessFirstSite?: boolean;

  openCompleteUpdateInEditor?(appId: string): void;
}

interface IManageInstalledAppsPageState {
  newInstalledApps: ManagedApp[];
  bundleApps: BundleWebSolution[];
  embeddedScriptAppsStatus: { [key: string]: IEmbeddedScriptAppStatus };
  isFetching: boolean;
  msAppId: string;
  openModal: ModalNames;
  modalProps?: IModalProps;
  permissions?: string[];
  siteIsPremium?: boolean;
}

@withTranslation
@withExperiments
export class ManageInstalledAppsPage extends React.Component<
  IManageInstalledAppsPageProps,
  IManageInstalledAppsPageState
> {
  constructor(props) {
    super(props);
    this.state = {
      newInstalledApps: [],
      bundleApps: [],
      isFetching: true,
      embeddedScriptAppsStatus: {},
      msAppId: '22bef345-3c5b-4c18-b782-74d4085112ff',
      openModal: ModalNames.NONE,
    };
  }

  convertEmbeddedScriptAppsStatus(apps) {
    const initialValue = {};
    return apps?.reduce((obj, app) => {
      const appId = (app as ManagedApp)?.appId;
      return {
        ...obj,
        [appId]: {
          status: app?.status,
          parameters: app?.parameters,
        },
      };
    }, initialValue);
  }

  async fetchEmbeddedScriptAppsStatusNew(apps: ManagedApp[]) {
    const { baseUrl, getAppInstance } = this.props;
    const signedInstance = getAppInstance(this.state.msAppId);

    const statusAsArray = await getAllEmbeddedScriptAppsStatus(
      signedInstance,
      baseUrl,
      apps,
    );
    return this.convertEmbeddedScriptAppsStatus(statusAsArray);
  }

  async componentDidMount() {
    const { newInstalledApps, embeddedScriptAppsStatus, bundleApps } =
      await this.fetchApps();

    const permissions = await getPermittedActions(
      this.props.baseUrl,
      this.props.metasiteId,
    );

    const siteData = await fetchSiteData(
      this.props.metasiteId,
      this.props.baseUrl,
    );

    this.setState({
      isFetching: false,
      newInstalledApps,
      embeddedScriptAppsStatus,
      permissions,
      siteIsPremium: siteData?.siteIsPremium,
      bundleApps,
    });

    this.props.onLoaded?.();

    sendOpenManageAppsBi(
      this.props.origin,
      this.props.metasiteId,
      this.props.editorType,
    );

    const allApps =
      this.props.hideBusinessSolutions && this.props.deepLinkData
        ? await fetchInstalledApps(
            this.props.baseUrl,
            this.props.getAppInstance(this.state.msAppId),
            this.props.locale,
            false,
          )
        : newInstalledApps;
    const deepLinkState = getDeepLink(this.props.deepLinkData, allApps);
    if (deepLinkState && deepLinkState.modalProps) {
      this.openModal(deepLinkState.openModal, deepLinkState.modalProps);
    }
  }

  componentDidUpdate() {
    if (this.props.origin === Origin.BIZMGR) {
      ModuleRegistry.notifyListeners('app-market.pending-apps-count-changed');
    }
  }

  closeModal = async () => {
    if (this.state.modalProps.openModalName === ModalNames.APP_SETTINGS) {
      this.fetchEmbeddedScriptAppsStatusNew(this.state.newInstalledApps).then(
        (data) => this.setState({ embeddedScriptAppsStatus: data }),
      );
    }

    this.setState({
      openModal: ModalNames.NONE,
      modalProps: { openModalName: ModalNames.NONE },
    });
  };

  openModal = (modalName: ModalNames, modalProps: IModalProps) => {
    const authorizationHeader = this.props.getAppInstance
      ? this.props.getAppInstance(this.state.msAppId)
      : '';
    const fullModalProps: IModalProps = {
      editorType: this.props.editorType,
      openModalName: modalName,
      closeModal: this.closeModal,
      baseUrl: this.props.baseUrl,
      authorizationHeader,
      origin: this.props.origin,
      editorUrl: this.props.editorUrl,
      manageAppsStaticsUrl: this.props.manageAppsStaticsUrl,
      deleteApp: this.deleteAppAndUpdatePage,
      locale: this.props.locale,
      msId: this.props.metasiteId,
      showUserActionNotification: this.props.showUserActionNotification,
      openDeleteModalAppInEditor: this.props.openDeleteModalAppInEditor,
      openCompleteUpdateInEditor: this.props.openCompleteUpdateInEditor,
      ...modalProps,
    };
    this.setState({
      openModal: modalProps.openModalName,
      modalProps: fullModalProps,
    });
  };

  async handleEmbeddedScriptToggle(
    appDefId: string,
    newStatus: boolean,
    appInstanceId: string,
    embeddedScriptAppsStatus: { [key: string]: IEmbeddedScriptAppStatus },
  ) {
    const msSignedInstance = this.props.getAppInstance(this.state.msAppId);
    if (msSignedInstance && appInstanceId) {
      return setEmbeddedScriptAppActive(
        newStatus,
        msSignedInstance,
        appInstanceId,
        this.props.baseUrl,
        embeddedScriptAppsStatus[appDefId],
      )
        .then((result) => {
          return {
            status: result.data.properties.disabled
              ? embeddedScriptAppStatus.DISABLED
              : embeddedScriptAppStatus.ENABLED,
            parameters: result.data.properties.parameters,
          };
        })
        .catch((e) => false);
    }
  }

  deleteAppByType = async (
    baseUrl: string,
    appToDelete: ManagedApp,
    authorizationHeader: string,
    editorType: string,
  ) => {
    if (
      (enableDeleteMLApp(appToDelete.appId, this.props.experiments) ||
        (appHasEditor(appToDelete) &&
          !this.props.isEditorless &&
          !(appToDelete.installationDetails.group === AppGroup.PENDING) &&
          editorType !== EditorType.ADI)) &&
      this.props.origin === Origin.EDITOR
    ) {
      return this.props.unInstallApp(appToDelete.appId).then(() => {
        if (
          !this.props.experiments.enabled('specs.app-market.UninstallFix') ||
          isEmbeddedScriptApp(appToDelete)
        ) {
          return newDeleteApp(baseUrl, authorizationHeader, appToDelete.appId);
        }
      });
    }
    return newDeleteApp(baseUrl, authorizationHeader, appToDelete.appId);
  };

  async fetchApps() {
    const {
      baseUrl,
      getAppInstance,
      locale,
      hideBusinessSolutions,
      geo,
      experiments,
      origin,
    } = this.props;

    const signedInstance = getAppInstance(this.state.msAppId);
    const geoString = '';
    const isDayfulFlow = origin === Origin.DAYFUL;
    let [installedApps, bundleApps, dayfulApps] = await Promise.all([
      fetchInstalledApps(
        baseUrl,
        signedInstance,
        locale,
        hideBusinessSolutions,
      ),
      fetchBundleApps(baseUrl, signedInstance, locale, geoString),
      isDayfulFlow
        ? fetchAppIdsByTag(
            baseUrl,
            signedInstance,
            locale,
            'e5c806a3-7ef7-4d32-bdc1-bbe913d93d48',
          )
        : [],
    ]);
    if (origin === Origin.DAYFUL) {
      installedApps = installedApps.filter((installedApp) => {
        return dayfulApps.tagApps.some(
          (tagApp) => tagApp.appId === installedApp.appId,
        );
      });
    }
    const enrichedBundleApps = newEnrichBundleApps(
      bundleApps as BundleWebSolution[],
      installedApps as ManagedApp[],
    );

    const embeddedScriptAppsStatus =
      await this.fetchEmbeddedScriptAppsStatusNew(installedApps);

    return {
      newInstalledApps: installedApps,
      embeddedScriptAppsStatus,
      bundleApps: enrichedBundleApps,
    };
  }

  deleteAppAndUpdatePage = (
    appToDelete: ManagedApp,
    showNotification: boolean,
  ) => {
    const {
      baseUrl,
      getAppInstance,
      experiments,
      locale,
      editorType,
      hideBusinessSolutions,
      geo,
      origin,
    } = this.props;
    const authorizationHeader = getAppInstance
      ? getAppInstance(this.state.msAppId)
      : '';
    const geoString = '';

    this.deleteAppByType(baseUrl, appToDelete, authorizationHeader, editorType)
      .then(async () => {
        const isDayfulFlow = origin === Origin.DAYFUL;
        let [installedApps, bundleApps, dayfulApps] = await Promise.all([
          fetchInstalledApps(
            baseUrl,
            authorizationHeader,
            locale,
            hideBusinessSolutions,
          ),
          fetchBundleApps(baseUrl, authorizationHeader, locale, geoString),
          isDayfulFlow
            ? fetchAppIdsByTag(
                baseUrl,
                authorizationHeader,
                locale,
                'e5c806a3-7ef7-4d32-bdc1-bbe913d93d48',
              )
            : [],
        ]);
        if (origin === Origin.DAYFUL) {
          installedApps = installedApps.filter((installedApp) => {
            return dayfulApps.tagApps.some(
              (tagApp) => tagApp.appId === installedApp.appId,
            );
          });
        }
        const enrichedBundleApps = newEnrichBundleApps(
          bundleApps as BundleWebSolution[],
          installedApps,
        );
        if (showNotification) {
          const appName = appToDelete?.appInfo?.name;
          this.props.showUserActionNotification({
            message: this.props
              .t('manage-apps.delete-modal.deletion-notification', { appName })
              .replace('&amp;', '&'),
            biName: 'delete-app',
            type: 'SUCCESS',
          });
        }

        this.setState({
          newInstalledApps: installedApps,
          bundleApps: enrichedBundleApps,
        });
      })
      .catch((e) => {
        if (showNotification) {
          const appName = appToDelete?.appInfo?.name;
          this.props.showUserActionNotification({
            message: this.props
              .t('manage-apps.delete-modal.deletion-error', { appName })
              .replace('&amp;', '&'),
            biName: 'delete-app',
            type: 'ERROR',
            action: {
              text: this.props.t('manage-apps.delete-modal.deletion-error-cta'),
              uiType: 'link',
              navigationTarget: {
                url: this.props.t(
                  'manage-apps.delete-modal.deletion-error-link',
                ),
              },
            },
            link: {
              caption: this.props.t(
                'manage-apps.delete-modal.deletion-error-cta',
              ),
              onNotificationLinkClick: () => {
                if (window) {
                  window.open(
                    this.props.t(
                      'manage-apps.delete-modal.deletion-error-link',
                    ),
                  );
                }
              },
            },
          });
        }
      });
  };

  updateEmbeddedScriptAppsStatus = (
    appDefId: string,
    newStatus: embeddedScriptAppStatus,
    appInstanceId: string,
  ) => {
    const isDisabled = newStatus === embeddedScriptAppStatus.DISABLED;
    this.handleEmbeddedScriptToggle(
      appDefId,
      isDisabled,
      appInstanceId,
      this.state.embeddedScriptAppsStatus,
    )
      .then((res) => {
        const status = { ...this.state.embeddedScriptAppsStatus };
        // @ts-expect-error
        const appStatus = res.status;
        // @ts-expect-error
        const newParameters = res.parameters;
        const newStatusEnum =
          appStatus === 'disabled'
            ? embeddedScriptAppStatus.DISABLED
            : embeddedScriptAppStatus.ENABLED;
        status[appDefId] = {
          status: newStatusEnum,
          parameters: newParameters,
        };
        this.setState({ embeddedScriptAppsStatus: status });
      })
      .catch((e) => console.log(e));
  };

  deleteAppFromBundle = (appId: string) => {
    const apps = this.state.bundleApps
      ? this.state.bundleApps.filter((app) => app.webSolutionBase.id !== appId)
      : [];
    this.setState({ bundleApps: apps });
  };

  refetchApps = async () => {
    this.setState({
      isFetching: true,
    });

    const { newInstalledApps, embeddedScriptAppsStatus, bundleApps } =
      await this.fetchApps();

    this.setState({
      isFetching: false,
      newInstalledApps,
      embeddedScriptAppsStatus,
      bundleApps,
    });
  };

  render() {
    if (this.state.isFetching) {
      return (
        <WixDesignSystemProvider
          features={{ newColorsBranding: true }}
          as="span"
        >
          <ModalsNew {...this.state.modalProps} />
          {this.props.referralInfo === 'marketing-integrations' && (
            <MarketingIntegrationsToast
              navigateToMarketingIntegration={
                this.props.navigateToMarketingIntegration
              }
            />
          )}
          <div className={s.loaderWrapper}>
            <Loader size="medium" />
          </div>
        </WixDesignSystemProvider>
      );
    }

    const completeSetupAppsCandidates = getCompleteSetupApps(
      this.state.newInstalledApps,
      this.state.bundleApps,
      this.state.siteIsPremium,
      this.props.experiments,
      origin === Origin.DAYFUL,
    );
    const completeSetupApps = this.props.isEditorless
      ? []
      : completeSetupAppsCandidates;
    const regularApps = (
      this.props.isEditorless
        ? // treat all pending installations apps as installed when there is no editor
          completeSetupAppsCandidates.map(
            (app) =>
              ({
                ...app,
                installationDetails: {
                  ...app.installationDetails,
                  group: AppGroup.INSTALLED,
                },
              } as ManagedApp),
          )
        : []
    ).concat(
      this.state.newInstalledApps?.filter(
        (app: ManagedApp) =>
          isRegularApp(app) &&
          completeSetupApps.findIndex(
            (completeSetupApp) => completeSetupApp.appId === app.appId,
          ) === -1,
      ),
    );
    const businessApps = this.state.newInstalledApps?.filter(
      (app: ManagedApp) =>
        isBusinessApp(app) &&
        completeSetupApps.findIndex(
          (completeSetupApp) => completeSetupApp.appId === app.appId,
        ) === -1,
    );
    const authorizationHeader = this.props.getAppInstance
      ? this.props.getAppInstance(this.state.msAppId)
      : '';

    return (
      <WixDesignSystemProvider features={{ newColorsBranding: true }} as="span">
        <ModalsNew {...this.state.modalProps} />
        {this.props.referralInfo === 'marketing-integrations' && (
          <MarketingIntegrationsToast
            navigateToMarketingIntegration={
              this.props.navigateToMarketingIntegration
            }
          />
        )}
        {this.state.newInstalledApps?.length > 0 ||
        this.state.bundleApps?.length > 0 ? (
          <Page>
            <Page.Header
              dataHook="manage-apps-page-header"
              title={
                this.props.experiments.enabled(
                  'specs.appMarket.manageAppsTextTest',
                )
                  ? this.props.t('manage-apps.page.title.test')
                  : this.props.t('manage-apps.page.title')
              }
              subtitle={
                this.props.origin === Origin.DAYFUL
                  ? this.props.t('manage-apps.page.dayful-subtitle')
                  : this.props.t('manage-apps.page.subtitle')
              }
              actionsBar={
                this.props.isEditorless ? null : (
                  <Button
                    priority={
                      this.props.experiments.enabled(
                        'specs.appMarket.manageAppsGetMoreAppsSkin',
                      )
                        ? null
                        : 'secondary'
                    }
                    onClick={() => {
                      sendAddAppBi(
                        this.props.origin,
                        this.props.metasiteId,
                        this.props.editorType,
                      );
                      this.props.goToHomePage();
                    }}
                    dataHook="manage-apps-go-to-app-market"
                  >
                    {this.props.t(
                      this.props.isEditorless
                        ? 'manage-apps.business-apps.editorless-card-header.title'
                        : 'manage-apps.get-more-apps',
                    )}
                  </Button>
                )
              }
            />
            <Page.Content>
              <PermissionsCtx.Provider value={this.state.permissions}>
                <AppsSection
                  isEditorless={this.props.isEditorless}
                  apps={completeSetupApps}
                  bundleApps={this.state.bundleApps}
                  origin={this.props.origin}
                  msid={this.props.metasiteId}
                  mainButtonAction={(app: ManagedApp) => {
                    const appHasBlocks = Boolean(getBuilderComps(app)?.length);

                    if (
                      this.props.origin === Origin.BIZMGR &&
                      appHasBlocks &&
                      isNewVersionAvailable(app)
                    ) {
                      this.openModal(ModalNames.UPDATE, { app });
                    } else {
                      this.props.mainButtonAction(
                        app,
                        this.state.bundleApps,
                        this.refetchApps,
                      );
                    }
                  }}
                  getAppInstance={this.props.getAppInstance}
                  openModal={this.openModal}
                  baseUrl={this.props.baseUrl}
                  // @ts-expect-error
                  deleteApp={this.deleteAppAndUpdatePage}
                  isBusinessApp={false}
                  editorType={this.props.editorType}
                  // @ts-expect-error
                  gotoWebSolutionPage={this.props.gotoWebSolutionPage}
                  updateEmbeddedScriptAppsStatus={
                    this.updateEmbeddedScriptAppsStatus
                  }
                  embeddedScriptAppsStatus={this.state.embeddedScriptAppsStatus}
                  siteIsPremium={this.state.siteIsPremium}
                  isActionRequiredSection={true}
                  openApp={this.props.openApp}
                  deleteAppFromBundle={this.deleteAppFromBundle}
                  siteIsBusinessFirstSite={this.props.siteIsBusinessFirstSite}
                  dataHook="pendingSection"
                  showUserActionNotification={
                    this.props.showUserActionNotification
                  }
                  editorUrl={this.props.editorUrl}
                  openDeleteModalAppInEditor={
                    this.props.openDeleteModalAppInEditor
                  }
                >
                  <CompleteSetupHeader
                    badgeCounter={completeSetupApps.length}
                  />
                </AppsSection>
                <AppsSection
                  isEditorless={this.props.isEditorless}
                  apps={regularApps}
                  bundleApps={this.state.bundleApps}
                  origin={this.props.origin}
                  msid={this.props.metasiteId}
                  mainButtonAction={(app: ManagedApp) =>
                    this.props.mainButtonAction(
                      app,
                      this.state.bundleApps,
                      this.refetchApps,
                    )
                  }
                  addAppAgain={this.props.addAppAgain}
                  getAppInstance={this.props.getAppInstance}
                  updateEmbeddedScriptAppsStatus={
                    this.updateEmbeddedScriptAppsStatus
                  }
                  embeddedScriptAppsStatus={this.state.embeddedScriptAppsStatus}
                  openModal={this.openModal}
                  baseUrl={this.props.baseUrl}
                  // @ts-expect-error
                  deleteApp={this.deleteAppAndUpdatePage}
                  isBusinessApp={false}
                  editorType={this.props.editorType}
                  // @ts-expect-error
                  gotoWebSolutionPage={this.props.gotoWebSolutionPage}
                  siteIsPremium={this.state.siteIsPremium}
                  dataHook="installedSection"
                  locale={this.props.locale}
                  authorizationHeader={authorizationHeader}
                  showUserActionNotification={
                    this.props.showUserActionNotification
                  }
                  editorUrl={this.props.editorUrl}
                  openDeleteModalAppInEditor={
                    this.props.openDeleteModalAppInEditor
                  }
                >
                  <RegularHeader />
                </AppsSection>
                {this.props.origin !== Origin.DAYFUL ? (
                  <>
                    <AppsSection
                      isEditorless={this.props.isEditorless}
                      apps={businessApps}
                      bundleApps={this.state.bundleApps}
                      origin={this.props.origin}
                      msid={this.props.metasiteId}
                      mainButtonAction={(app: ManagedApp) =>
                        this.props.mainButtonAction(
                          app,
                          this.state.bundleApps,
                          this.refetchApps,
                        )
                      }
                      addAppAgain={this.props.addAppAgain}
                      getAppInstance={this.props.getAppInstance}
                      openModal={this.openModal}
                      baseUrl={this.props.baseUrl}
                      // @ts-expect-error
                      deleteApp={this.deleteAppAndUpdatePage}
                      isBusinessApp={true}
                      editorType={this.props.editorType}
                      // @ts-expect-error
                      gotoWebSolutionPage={this.props.gotoWebSolutionPage}
                      siteIsPremium={this.state.siteIsPremium}
                      dataHook="businessSolutionsSection"
                      showUserActionNotification={
                        this.props.showUserActionNotification
                      }
                      editorUrl={this.props.editorUrl}
                      openDeleteModalAppInEditor={
                        this.props.openDeleteModalAppInEditor
                      }
                    >
                      <BusinessHeader />
                    </AppsSection>
                    {!this.props.isEditorless && (
                      <>
                        <Box
                          align="space-between"
                          verticalAlign="middle"
                          marginTop="48px"
                          marginBottom="18px"
                        >
                          <Heading
                            dataHook="recommended-apps-section-title"
                            appearance="H3"
                          >
                            {this.props.t('manage-apps.recommended-for-you')}
                          </Heading>
                          <TextButton
                            suffixIcon={<ChevronRight />}
                            onClick={() => {
                              sendAddAppBi(
                                this.props.origin,
                                this.props.metasiteId,
                                this.props.editorType,
                              );
                              this.props.goToHomePage();
                            }}
                          >
                            {this.props.t('manage-apps.see-all-apps')}
                          </TextButton>
                        </Box>
                        <div style={{ marginBottom: '12px' }}>
                          <Divider />
                        </div>
                        <RecommendedApps
                          baseUrl={this.props.baseUrl}
                          authorizationHeader={authorizationHeader}
                          locale={this.props.locale}
                          origin={this.props.origin}
                          gotoWebSolutionPage={this.props.gotoWebSolutionPage}
                          editorType={this.props.editorType}
                          msid={this.props.metasiteId}
                        />
                      </>
                    )}
                  </>
                ) : null}
              </PermissionsCtx.Provider>
            </Page.Content>
          </Page>
        ) : (
          <EmptyState
            isEditorless={this.props.isEditorless}
            baseUrl={this.props.baseUrl}
            msid={this.props.metasiteId}
            locale={this.props.locale}
            // @ts-expect-error
            gotoWebSolutionPage={this.props.gotoWebSolutionPage}
            goToHomePage={this.props.goToHomePage}
            origin={this.props.origin}
            editorType={this.props.editorType}
            authorizationHeader={authorizationHeader}
          />
        )}
      </WixDesignSystemProvider>
    );
  }
}

function sendAddAppBi(origin: Origin, msid: string, editorType: string) {
  const biLogger = webBiLogger.factory().logger();
  biLogger
    .report(
      manageAppsAddApp({
        market:
          origin === Origin.BIZMGR
            ? editorType === EditorType.ADI
              ? 'adi_dashboard'
              : 'dashboard'
            : origin,
        msid,
        referral_info: 'manage_apps',
      }),
    )
    .catch((e) => console.log(e));
}

function sendOpenManageAppsBi(
  origin: Origin,
  msid: string,
  editorType: string,
) {
  const biLogger = webBiLogger.factory().logger();
  biLogger
    .report(
      appMarketOpenManageApps({
        msid,
        market:
          origin === Origin.BIZMGR
            ? editorType === EditorType.ADI
              ? 'adi_dashboard'
              : 'dashboard'
            : origin,
      }),
    )
    .catch((e) => console.log(e));
}
